import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Store, Calendar, ShieldOff, ShieldCheck,
  Trash2, Loader2, ChevronUp, ChevronDown, UserX, RefreshCw, KeyRound,
  Pencil, X, CreditCard, Coins, Crown, Clock, History, Eraser,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { adminFetch, ConfirmOtpModal } from "./adminUtils";

// ─── Fila de usuario ──────────────────────────────────────────────────
function UserRow({ user, onBlock, onDelete, onResetPassword, onResetData, onEdit }) {
  const [blockLoading, setBlockLoading] = useState(false);

  const handleBlock = async () => {
    setBlockLoading(true);
    try { await onBlock(user.user_id); }
    finally { setBlockLoading(false); }
  };

  const initials = (user.name || user.email || "?")
    .split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div
      data-testid={`user-row-${user.user_id}`}
      className={`px-5 py-4 flex items-center gap-4 transition-colors
        ${user.blocked ? "bg-red-950/10" : "hover:bg-slate-800/30"}`}
    >
      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold
        ${user.blocked ? "bg-red-900/40 text-red-400" : "bg-slate-800 text-slate-300"}`}>
        {user.picture
          ? <img src={user.picture} alt={`Avatar de ${user.name || 'usuario'}`} className="w-full h-full rounded-full object-cover" />
          : initials}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-slate-200 text-sm font-medium truncate">{user.name || "—"}</p>
          {user.blocked && (
            <span className="text-[10px] font-semibold text-red-400 bg-red-500/10
                             border border-red-500/20 rounded-full px-1.5 py-0.5 flex-shrink-0">
              BLOQUEADO
            </span>
          )}
        </div>
        <p className="text-muted-foreground text-xs truncate">{user.email}</p>
      </div>

      <div className="flex items-center gap-1.5 w-20 flex-shrink-0" data-testid={`restaurants-count-${user.user_id}`}>
        <Store className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
        <span className={`text-sm font-medium ${user.restaurants.length > 0 ? "text-slate-300" : "text-foreground"}`}>
          {user.restaurants.length}
        </span>
      </div>

      <div className="hidden sm:flex items-center gap-1.5 w-28 flex-shrink-0">
        <Calendar className="w-3 h-3 text-muted-foreground" />
        <span className="text-muted-foreground text-xs">
          {user.created_at
            ? new Date(user.created_at).toLocaleDateString("es-ES",
                { day: "2-digit", month: "short", year: "numeric" })
            : "—"}
        </span>
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={() => onEdit(user)}
          data-testid={`edit-btn-${user.user_id}`}
          title="Editar usuario"
          className="p-1.5 rounded-lg text-muted-foreground hover:text-orange-400 hover:bg-orange-500/10 transition-colors"
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button
          onClick={() => onResetPassword(user)}
          data-testid={`reset-pw-btn-${user.user_id}`}
          title="Resetear contraseña"
          className="p-1.5 rounded-lg text-muted-foreground hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors"
        >
          <KeyRound className="w-4 h-4" />
        </button>
        <button
          onClick={() => onResetData(user)}
          data-testid={`reset-data-btn-${user.user_id}`}
          title="Borrar datos del usuario y resetear a Starter"
          className="p-1.5 rounded-lg text-muted-foreground hover:text-fuchsia-400 hover:bg-fuchsia-500/10 transition-colors"
        >
          <Eraser className="w-4 h-4" />
        </button>
        <button
          onClick={handleBlock}
          disabled={blockLoading}
          data-testid={`block-btn-${user.user_id}`}
          title={user.blocked ? "Desbloquear usuario" : "Bloquear usuario"}
          className={`p-1.5 rounded-lg transition-colors ${
            user.blocked
              ? "text-amber-400 hover:bg-amber-500/10"
              : "text-muted-foreground hover:text-amber-400 hover:bg-amber-500/10"
          }`}
        >
          {blockLoading
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : user.blocked
              ? <ShieldCheck className="w-4 h-4" />
              : <ShieldOff className="w-4 h-4" />}
        </button>
        <button
          onClick={() => onDelete(user)}
          data-testid={`delete-btn-${user.user_id}`}
          title="Eliminar usuario"
          className="p-1.5 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Modal de edición de usuario ──────────────────────────────────────
function EditUserModal({ user, onClose, onSaved }) {
  const [tab, setTab] = useState("profile");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState([]);

  // Profile form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  // Subscription form
  const [selectedRestaurant, setSelectedRestaurant] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("");
  const [customCredits, setCustomCredits] = useState("");
  const [savingSub, setSavingSub] = useState(false);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      adminFetch(`/api/admin/users/${user.user_id}`),
      adminFetch(`/api/admin/users/${user.user_id}/subscription-history`),
    ]).then(([data, hist]) => {
        setDetail(data);
        setHistory(hist.history || []);
        setName(data.user.name || "");
        setEmail(data.user.email || "");
        if (data.restaurants?.length > 0) {
          setSelectedRestaurant(data.restaurants[0].restaurant_id);
        }
        if (data.available_plans?.length > 0) {
          setSelectedPlan(data.available_plans[0].plan_id);
        }
      })
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [user]);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await adminFetch(`/api/admin/users/${user.user_id}`, {
        method: "PUT",
        body: JSON.stringify({ name, email }),
      });
      toast.success("Datos del usuario actualizados");
      onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setSavingProfile(false); }
  };

  const handleAssignSubscription = async () => {
    if (!selectedRestaurant || !selectedPlan) {
      toast.error("Selecciona restaurante y plan"); return;
    }
    setSavingSub(true);
    try {
      const body = {
        restaurant_id: selectedRestaurant,
        plan_id: selectedPlan,
      };
      if (customCredits) body.custom_credits = parseInt(customCredits, 10);
      await adminFetch(`/api/admin/users/${user.user_id}/assign-subscription`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      toast.success("Suscripcion asignada correctamente");
      // Reload detail and history
      const [data, hist] = await Promise.all([
        adminFetch(`/api/admin/users/${user.user_id}`),
        adminFetch(`/api/admin/users/${user.user_id}/subscription-history`),
      ]);
      setDetail(data);
      setHistory(hist.history || []);
      setCustomCredits("");
      onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setSavingSub(false); }
  };

  const selectedPlanObj = detail?.available_plans?.find(p => p.plan_id === selectedPlan);
  const currentSub = detail?.subscriptions?.find(s => s.restaurant_id === selectedRestaurant);

  if (!user) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="edit-user-modal">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg mx-4 shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-orange-500/15 rounded-lg flex items-center justify-center">
              <Pencil className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">Editar Usuario</h3>
              <p className="text-muted-foreground text-xs">{user.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-slate-300 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-800">
          {[
            { id: "profile", label: "Datos", icon: Pencil },
            { id: "subscription", label: "Suscripcion", icon: Crown },
            { id: "history", label: "Historial", icon: Clock },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`edit-user-tab-${t.id}`}
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors
                ${tab === t.id
                  ? "text-orange-400 border-b-2 border-orange-400"
                  : "text-muted-foreground hover:text-slate-300"}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : tab === "profile" ? (
            <div className="space-y-4">
              <div>
                <label className="text-slate-400 text-xs uppercase tracking-wide mb-1.5 block">Nombre</label>
                <Input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  data-testid="edit-user-name"
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="text-slate-400 text-xs uppercase tracking-wide mb-1.5 block">Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  data-testid="edit-user-email"
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600 focus:border-orange-500"
                />
              </div>

              {detail?.restaurants?.length > 0 && (
                <div>
                  <label className="text-slate-400 text-xs uppercase tracking-wide mb-1.5 block">Restaurantes</label>
                  <div className="space-y-1">
                    {detail.restaurants.map(r => (
                      <div key={r.restaurant_id} className="flex items-center gap-2 text-sm text-slate-300 bg-slate-800/50 rounded-lg px-3 py-2">
                        <Store className="w-3.5 h-3.5 text-muted-foreground" />
                        {r.name}
                        <span className="text-muted-foreground text-xs ml-auto">{r.restaurant_id}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  data-testid="save-user-profile-btn"
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  {savingProfile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Guardar cambios
                </Button>
              </div>
            </div>
          ) : tab === "subscription" ? (
            <div className="space-y-4">
              {detail?.restaurants?.length > 0 ? (
                <div>
                  <label className="text-slate-400 text-xs uppercase tracking-wide mb-1.5 block">Restaurante</label>
                  <select
                    value={selectedRestaurant}
                    onChange={e => setSelectedRestaurant(e.target.value)}
                    data-testid="edit-user-restaurant-select"
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm focus:border-orange-500 focus:outline-none"
                  >
                    {detail.restaurants.map(r => (
                      <option key={r.restaurant_id} value={r.restaurant_id}>{r.name}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  Este usuario no tiene restaurantes
                </div>
              )}

              {/* Current subscription info */}
              {currentSub?.subscription && (
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3">
                  <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Suscripcion actual</p>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Crown className="w-4 h-4 text-amber-400" />
                      <span className="text-white text-sm font-medium">{currentSub.subscription.plan_name}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                      ${currentSub.subscription.status === "active" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>
                      {currentSub.subscription.status === "active" ? "Activa" : currentSub.subscription.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <Coins className="w-3 h-3" />
                      {currentSub.subscription.credits_total - (currentSub.subscription.credits_used || 0)} / {currentSub.subscription.credits_total} creditos
                    </span>
                    <span>
                      Hasta {new Date(currentSub.subscription.period_end).toLocaleDateString("es-ES")}
                    </span>
                  </div>
                </div>
              )}

              {detail?.restaurants?.length > 0 && (
                <>
                  {/* Plan selector */}
                  <div>
                    <label className="text-slate-400 text-xs uppercase tracking-wide mb-1.5 block">
                      {currentSub?.subscription ? "Cambiar plan" : "Asignar plan"}
                    </label>
                    {detail.available_plans?.length > 0 ? (
                      <div className="space-y-2">
                        {detail.available_plans.map(p => (
                          <button
                            key={p.plan_id}
                            onClick={() => { setSelectedPlan(p.plan_id); setCustomCredits(""); }}
                            data-testid={`plan-option-${p.plan_id}`}
                            className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all
                              ${selectedPlan === p.plan_id
                                ? "border-orange-500 bg-orange-500/10"
                                : "border-slate-700 bg-slate-800/30 hover:border-slate-600"}`}
                          >
                            <Crown className={`w-4 h-4 flex-shrink-0 ${selectedPlan === p.plan_id ? "text-orange-400" : "text-muted-foreground"}`} />
                            <div className="flex-1">
                              <p className={`text-sm font-medium ${selectedPlan === p.plan_id ? "text-white" : "text-slate-300"}`}>
                                {p.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {p.price > 0 ? `${p.price}€/mes` : "Gratuito"} · {p.credits_monthly} creditos/mes
                              </p>
                            </div>
                            {selectedPlan === p.plan_id && (
                              <div className="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0" />
                            )}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm py-2">No hay planes disponibles. Crea uno en la seccion Planes.</p>
                    )}
                  </div>

                  {/* Custom credits */}
                  <div>
                    <label className="text-slate-400 text-xs uppercase tracking-wide mb-1.5 block">
                      Creditos personalizados <span className="text-muted-foreground">(opcional)</span>
                    </label>
                    <Input
                      type="number"
                      value={customCredits}
                      onChange={e => setCustomCredits(e.target.value)}
                      placeholder={selectedPlanObj ? `Por defecto: ${selectedPlanObj.credits_monthly}` : "Creditos"}
                      data-testid="edit-user-custom-credits"
                      className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600 focus:border-orange-500"
                    />
                    <p className="text-muted-foreground text-xs mt-1">
                      Deja vacio para usar los creditos del plan ({selectedPlanObj?.credits_monthly || 0})
                    </p>
                  </div>

                  {/* Assign button */}
                  <div className="flex justify-end pt-2">
                    <Button
                      onClick={handleAssignSubscription}
                      disabled={savingSub || !selectedPlan || !selectedRestaurant}
                      data-testid="assign-subscription-btn"
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      {savingSub ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                      {currentSub?.subscription ? "Cambiar suscripcion" : "Asignar suscripcion"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : (
            /* History tab */
            <div className="space-y-4" data-testid="subscription-history-tab">
              {history.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Sin historial de cambios</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {history.map((h) => (
                    <div key={h.log_id} className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Crown className="w-3.5 h-3.5 text-orange-400" />
                          <span className="text-white text-sm font-medium">{h.plan_name}</span>
                        </div>
                        <span className="text-muted-foreground text-xs">
                          {new Date(h.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Store className="w-3 h-3" /> {h.restaurant_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Coins className="w-3 h-3" /> {h.credits_total} creditos
                        </span>
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Por: {h.admin_email}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Pagina principal ─────────────────────────────────────────────────
export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortDir, setSortDir] = useState("desc");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [resetDataTarget, setResetDataTarget] = useState(null);
  const [showResetDataOtpModal, setShowResetDataOtpModal] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const fetchedRef = useRef(false);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await adminFetch("/api/admin/users");
      setUsers(data.users || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    loadUsers();
  }, []); // eslint-disable-line

  const handleBlock = async (userId) => {
    try {
      const res = await adminFetch(`/api/admin/users/${userId}/block`, { method: "POST" });
      setUsers((prev) =>
        prev.map((u) => u.user_id === userId ? { ...u, blocked: res.blocked } : u)
      );
      toast.success(res.blocked ? "Usuario bloqueado" : "Usuario desbloqueado");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteRequest = (user) => {
    setDeleteTarget(user);
    setShowOtpModal(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("La contrasena debe tener al menos 6 caracteres");
      return;
    }
    setResetLoading(true);
    try {
      const res = await adminFetch(`/api/admin/users/${resetTarget.user_id}/reset-password`, {
        method: "POST",
        body: JSON.stringify({ new_password: newPassword }),
      });
      toast.success(res.message || "Contrasena actualizada");
      setResetTarget(null);
      setNewPassword("");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setResetLoading(false);
    }
  };

  const handleDeleteConfirmed = async (sessionId, otp) => {
    await adminFetch(`/api/admin/users/${deleteTarget.user_id}`, {
      method: "DELETE",
      body: JSON.stringify({ session_id: sessionId, otp }),
    });
    setUsers((prev) => prev.filter((u) => u.user_id !== deleteTarget.user_id));
    toast.success(`Usuario ${deleteTarget.email} eliminado`);
    setDeleteTarget(null);
  };

  const handleResetDataRequest = (user) => {
    setResetDataTarget(user);
    setShowResetDataOtpModal(true);
  };

  const handleResetDataConfirmed = async (sessionId, otp) => {
    const res = await adminFetch(`/api/admin/users/${resetDataTarget.user_id}/reset-data`, {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, otp }),
    });
    toast.success(
      `Datos de ${res.email} borrados (${res.total_deleted} docs, ${res.restaurants_wiped} rest.) → ${res.new_plan}`,
    );
    setResetDataTarget(null);
    loadUsers();
  };

  const filtered = users
    .filter((u) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const da = a.created_at || "";
      const db2 = b.created_at || "";
      return sortDir === "desc" ? db2.localeCompare(da) : da.localeCompare(db2);
    });

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-white text-2xl font-bold">Usuarios</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {users.length} usuario{users.length !== 1 ? "s" : ""} registrado{users.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button
          onClick={() => { fetchedRef.current = false; loadUsers(); }}
          data-testid="admin-refresh-users"
          className="flex items-center gap-2 text-muted-foreground hover:text-slate-300 text-sm
                     transition-colors px-3 py-1.5 rounded-lg hover:bg-slate-800"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o email..."
            data-testid="users-search-input"
            className="pl-9 bg-slate-900 border-slate-700 text-white placeholder:text-slate-600
                       focus:border-orange-500"
          />
        </div>
        <button
          onClick={() => setSortDir((d) => d === "desc" ? "asc" : "desc")}
          data-testid="users-sort-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 border border-slate-700
                     text-slate-400 hover:text-white hover:border-slate-600 text-sm transition-colors"
        >
          <Calendar className="w-4 h-4" />
          Fecha {sortDir === "desc" ? "↓ reciente" : "↑ antigua"}
          {sortDir === "desc"
            ? <ChevronDown className="w-3 h-3" />
            : <ChevronUp className="w-3 h-3" />}
        </button>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-800 grid
            grid-cols-[auto_1fr_auto_auto] sm:grid-cols-[auto_1fr_5rem_7rem_auto] gap-4 items-center">
          <div className="w-9" />
          <p className="text-muted-foreground text-xs uppercase tracking-wide">Usuario</p>
          <p className="hidden sm:block text-muted-foreground text-xs uppercase tracking-wide">Rest.</p>
          <p className="hidden sm:block text-muted-foreground text-xs uppercase tracking-wide">Registro</p>
          <p className="text-muted-foreground text-xs uppercase tracking-wide text-right">Acciones</p>
        </div>

        {loading ? (
          <div className="py-12 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center">
            <UserX className="w-10 h-10 text-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">
              {search ? "Sin resultados para esa busqueda" : "No hay usuarios registrados"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800/60">
            {filtered.map((user) => (
              <UserRow
                key={user.user_id}
                user={user}
                onBlock={handleBlock}
                onDelete={handleDeleteRequest}
                onResetPassword={(u) => { setResetTarget(u); setNewPassword(""); }}
                onResetData={handleResetDataRequest}
                onEdit={(u) => setEditTarget(u)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {editTarget && (
        <EditUserModal
          user={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => { loadUsers(); }}
        />
      )}

      {/* OTP confirm modal — Delete user */}
      <ConfirmOtpModal
        open={showOtpModal}
        onClose={() => { setShowOtpModal(false); setDeleteTarget(null); }}
        onConfirmed={handleDeleteConfirmed}
        title="Eliminar usuario"
        description={
          deleteTarget
            ? `Vas a eliminar permanentemente a ${deleteTarget.email}. Esta accion no se puede deshacer. Introduce el codigo de confirmacion que recibiras en tu email.`
            : ""
        }
      />

      {/* OTP confirm modal — Reset user data */}
      <ConfirmOtpModal
        open={showResetDataOtpModal}
        onClose={() => { setShowResetDataOtpModal(false); setResetDataTarget(null); }}
        onConfirmed={handleResetDataConfirmed}
        title="Borrar datos del usuario"
        description={
          resetDataTarget
            ? `Vas a borrar TODOS los datos de ${resetDataTarget.email} (restaurantes, menus, ventas, finanzas, inventario, fidelizacion, etc.) y resetear su suscripcion a Starter limpio. La cuenta del usuario NO se elimina. Esta accion no se puede deshacer. Introduce el codigo que recibiras en tu email.`
            : ""
        }
      />

      {/* Reset Password Modal */}
      {resetTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 bg-cyan-500/15 rounded-lg flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Resetear Contrasena</h3>
                <p className="text-muted-foreground text-xs">{resetTarget.email}</p>
              </div>
            </div>
            <Input
              data-testid="reset-password-input"
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Nueva contrasena (min. 6 caracteres)"
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600 mb-4"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleResetPassword()}
            />
            <div className="flex gap-2 justify-end">
              <Button
                variant="ghost"
                onClick={() => setResetTarget(null)}
                className="text-slate-400 hover:text-white"
              >
                Cancelar
              </Button>
              <Button
                data-testid="confirm-reset-password-btn"
                onClick={handleResetPassword}
                disabled={resetLoading || newPassword.length < 6}
                className="bg-cyan-600 hover:bg-cyan-500 text-white"
              >
                {resetLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Confirmar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
